import React, { useEffect, useRef, useState, useMemo } from "react";
import ExcercisePlanLoader from "@components/ui/Loaders/ExcercisePlanLoader";
import { toast } from "react-toastify";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import ConfirmAdvise from "@components/ui/ConfimDialogueAdvise";
import { Edit } from "lucide-react";
import { NavigationPrompt } from "@utils/NavigationBlocker";
import {
  IconButton,
  InputAdornment,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { getAllFormFields, updateAllFormFields } from "api/preferences";
import { getAutoPreference } from "api/generalServices";
import MainHeader from "@components/ui/MainHeader";
import HeaderImage from "@assets/images/AdviseMeHeader.svg";
import AdviseMeSidePic from "@assets/images/AdviseMeSidePic.svg";
import {
  BmiCategory,
  GoalOverview,
  NutritionStratergy,
  WorkoutFocus,
  LongTerm,
} from "@utils/defaultImages";
import { getEquipmentMetadata, getWorkoutMetadata } from "api/exercisePlan";
import { useUI } from "context/LoadingContext";
import { useMeta } from "context/ExerciseMeta";
import MedicalViewMultipSelectDropdown from "@components/ui/customDropdowns/MedicalViewMultipSelectDropdown";
import ExercisePlanMultiSelectDropdown from "@components/ui/customDropdowns/ExercisePlanMultiSelectDropdown";
import SportsGymnasticsIcon from "@mui/icons-material/SportsGymnastics";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import TuneIcon from "@mui/icons-material/Tune";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const AdviseMe = () => {
  const [isAdviceLoading, setIsAdviceLoading] = useState(false);
  const { setIsActionLocked } = useUI();
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [selectedTags, setSelectedTags] = useState({});
  const [message, setMessage] = useState(null);
  const [advisedConfirm, setAdviseConfirm] = useState(null);
  const [autoSubmit, setAutoSubmit] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { dynamicOptions } = useMeta();
  const countdownRef = useRef(null);
  const { t } = useTranslation();

  // Exercise time options
  const exerciseTimeOptions = [
    "15 Minutes",
    "30 Minutes",
    "45 Minutes",
    "60 Minutes",
    "90 Minutes",
    "90+ Minutes",
  ];

  // Days options
  const daysOptions = [
    "All Days",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Timeline options
  const timelineOptions = [
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "09",
    "10",
    "11",
    "12",
  ];

  // Duration type options
  const durationTypeOptions = ["Week", "Month"];

  // Helper function to get field value
  const getFieldValue = (field) => {
    return selectedTags?.[field] || "";
  };

  // Helper function to set field value
  const setFieldValue = (field, value) => {
    setSelectedTags((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Calculate daily calorie adjustment from selectedTags
  const calculatedDailyCalorieAdjustment = useMemo(() => {
    const target = Number(getFieldValue("targetDailyCalories"));
    const maintenance = Number(getFieldValue("maintenanceCalories"));
    if (
      !isNaN(target) &&
      !isNaN(maintenance) &&
      target !== 0 &&
      maintenance !== 0
    ) {
      return Math.round(target - maintenance);
    }
    return 0;
  }, [selectedTags?.targetDailyCalories, selectedTags?.maintenanceCalories]);

  // Sync calculated value to selectedTags
  useEffect(() => {
    if (
      calculatedDailyCalorieAdjustment !==
      getFieldValue("dailyCalorieAdjustment")
    ) {
      setFieldValue("dailyCalorieAdjustment", calculatedDailyCalorieAdjustment);
    }
  }, [calculatedDailyCalorieAdjustment]);

  useEffect(() => {
    return () => {
      setIsActionLocked(false);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await getAllFormFields();
      const tags = data?.selectedPreferences || {};
      setSelectedTags(tags);

      if (tags?.adviseMeDescription) {
        setMessage(tags.adviseMeDescription);
      }
    } catch (error) {
      console.error(
        "Error fetching diet plan:",
        error.response ? error.response.data : error.message
      );
    } finally {
      setFirstLoad(false);
    }
  };

  const validateSingleField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case "targetWeight":
        if (!value || value === "") {
          newErrors.targetWeight = t("validation.required", {
            field: t(`firstFormSteps.targetWeight.label`),
          });
        } else {
          delete newErrors.targetWeight;
        }
        break;

      case "maintenanceCalories":
        if (!value || value === "") {
          newErrors.maintenanceCalories = t("validation.required", {
            field: t(`firstFormSteps.maintenanceCalories.label`),
          });
        } else {
          delete newErrors.maintenanceCalories;
        }
        break;

      case "targetDailyCalories":
        if (!value || value === "") {
          newErrors.targetDailyCalories = t("validation.required", {
            field: t(`firstFormSteps.targetDailyCalories.label`),
          });
        } else {
          delete newErrors.targetDailyCalories;
        }
        break;

      case "timeline":
        if (!value || value === "") {
          newErrors.timeline = t("validation.required", {
            field: t(`firstFormSteps.timeline.label`),
          });
        } else {
          delete newErrors.timeline;
        }
        break;

      case "durationType":
        if (!value || value === "") {
          newErrors.durationType = t("validation.required", {
            field: t(`firstFormSteps.durationType.label`),
          });
        } else {
          delete newErrors.durationType;
        }
        break;

      case "goalBodyFat":
        if (!value || value === "") {
          newErrors.goalBodyFat = t("validation.required", {
            field: t("Goal Body Fat %"),
          });
        } else {
          delete newErrors.goalBodyFat;
        }
        break;

      case "goalWaistCircumference":
        if (!value || value === "") {
          newErrors.goalWaistCircumference = t("validation.required", {
            field: t("Goal Waist Circumference (cm)"),
          });
        } else {
          delete newErrors.goalWaistCircumference;
        }
        break;

      case "goalSteps":
        if (!value || value === "") {
          newErrors.goalSteps = t("validation.required", {
            field: t("preferences.fields.goalSteps"),
          });
        } else {
          delete newErrors.goalSteps;
        }
        break;

      case "detailTargetBurnCalories":
        if (!value || value === "") {
          newErrors.detailTargetBurnCalories = t("validation.required", {
            field: t("preferences.fields.detailTargetBurnCalories"),
          });
        } else {
          delete newErrors.detailTargetBurnCalories;
        }
        break;

      case "targetActiveTime":
        if (!value || value === "") {
          newErrors.targetActiveTime = t("validation.required", {
            field: t("preferences.fields.targetActiveTime"),
          });
        } else {
          delete newErrors.targetActiveTime;
        }
        break;

      case "dailyExerciseTimePreference":
        if (!value || value === "") {
          newErrors.dailyExerciseTimePreference = t("validation.required", {
            field: t("Daily Exercise Time Preference"),
          });
        } else {
          delete newErrors.dailyExerciseTimePreference;
        }
        break;

      case "equipmentCategories":
        if (!value || value === "") {
          newErrors.equipmentCategories = t("validation.required", {
            field: t("Equipment Category"),
          });
        } else {
          delete newErrors.equipmentCategories;
        }
        break;

      case "difficultiesLevel":
        if (!value || value === "") {
          newErrors.difficultiesLevel = t("validation.required", {
            field: t("Difficulty Level"),
          });
        } else {
          delete newErrors.difficultiesLevel;
        }
        break;

      default:
        break;
    }

    // For array fields (special handling)
    if (field === "workoutsCategories") {
      if (!value || value.length < 1) {
        newErrors.workoutsCategories = t("validation.required", {
          field: t("Workout Categories"),
        });
      } else {
        delete newErrors.workoutsCategories;
      }
    }

    if (field === "selectableDays") {
      if (!value || value.length < 1) {
        newErrors.selectableDays = t("validation.required", {
          field: t("Training Frequency"),
        });
      } else {
        delete newErrors.selectableDays;
      }
    }

    setErrors(newErrors);
  };

  // Updated handleInputChange for text/number inputs
  const handleInputChange = (field, value) => {
    // Update the field value
    setFieldValue(field, value);

    // Validate the field immediately
    validateSingleField(field, value);
  };

  // Updated handleSelectChange for dropdowns
  const handleSelectChange = (field, value) => {
    setFieldValue(field, value);

    // Validate the field immediately
    validateSingleField(field, value);
  };

  // Updated handleMultiSelectChange
  const handleMultiSelectChange = (field, values) => {
    let processedValues = [...values];

    if (field === "selectableDays") {
      const lastSelected = values[values.length - 1];

      if (lastSelected === "All Days") {
        processedValues = ["All Days"];
      } else {
        processedValues = values.filter((v) => v !== "All Days");
      }
    }

    setFieldValue(field, processedValues);

    // Validate the field immediately
    validateSingleField(field, processedValues);
  };

  // Updated validateForm (just for final validation before submit)
  const validateForm = () => {
    // Clear all errors first
    setErrors({});

    const newErrors = {};

    // Validate all required fields
    if (
      !getFieldValue("targetWeight") ||
      getFieldValue("targetWeight") === ""
    ) {
      newErrors.targetWeight = t("validation.required", {
        field: t(`firstFormSteps.targetWeight.label`),
      });
    }

    if (
      !getFieldValue("maintenanceCalories") ||
      getFieldValue("maintenanceCalories") === ""
    ) {
      newErrors.maintenanceCalories = t("validation.required", {
        field: t(`firstFormSteps.maintenanceCalories.label`),
      });
    }

    if (
      !getFieldValue("targetDailyCalories") ||
      getFieldValue("targetDailyCalories") === ""
    ) {
      newErrors.targetDailyCalories = t("validation.required", {
        field: t(`firstFormSteps.targetDailyCalories.label`),
      });
    }

    if (!getFieldValue("timeline") || getFieldValue("timeline") === "") {
      newErrors.timeline = t("validation.required", {
        field: t(`firstFormSteps.timeline.label`),
      });
    }

    if (
      !getFieldValue("durationType") ||
      getFieldValue("durationType") === ""
    ) {
      newErrors.durationType = t("validation.required", {
        field: t(`firstFormSteps.durationType.label`),
      });
    }

    if (!getFieldValue("goalBodyFat") || getFieldValue("goalBodyFat") === "") {
      newErrors.goalBodyFat = t("validation.required", {
        field: t("Goal Body Fat %"),
      });
    }

    if (
      !getFieldValue("goalWaistCircumference") ||
      getFieldValue("goalWaistCircumference") === ""
    ) {
      newErrors.goalWaistCircumference = t("validation.required", {
        field: t("Goal Waist Circumference (cm)"),
      });
    }

    if (!getFieldValue("goalSteps") || getFieldValue("goalSteps") === "") {
      newErrors.goalSteps = t("validation.required", {
        field: `preferences.fields.goalSteps`,
      });
    }
    if (
      !getFieldValue("detailTargetBurnCalories") ||
      getFieldValue("detailTargetBurnCalories") === ""
    ) {
      newErrors.detailTargetBurnCalories = t("validation.required", {
        field: t("preferences.fields.detailTargetBurnCalories"),
      });
    }
    if (
      !getFieldValue("targetActiveTime") ||
      getFieldValue("targetActiveTime") === ""
    ) {
      newErrors.targetActiveTime = t("validation.required", {
        field: t("preferences.fields.targetActiveTime"),
      });
    }

    // Exercise preferences validations
    if (
      !getFieldValue("dailyExerciseTimePreference") ||
      getFieldValue("dailyExerciseTimePreference") === ""
    ) {
      newErrors.dailyExerciseTimePreference = t("validation.required", {
        field: t("Daily Exercise Time Preference"),
      });
    }

    if (
      !getFieldValue("workoutsCategories") ||
      getFieldValue("workoutsCategories").length < 1
    ) {
      newErrors.workoutsCategories = t("validation.required", {
        field: t("Workout Categories"),
      });
    }

    if (
      !getFieldValue("equipmentCategories") ||
      getFieldValue("equipmentCategories") === ""
    ) {
      newErrors.equipmentCategories = t("validation.required", {
        field: t("Equipment Category"),
      });
    }

    if (
      !getFieldValue("difficultiesLevel") ||
      getFieldValue("difficultiesLevel") === ""
    ) {
      newErrors.difficultiesLevel = t("validation.required", {
        field: t("Difficulty Level"),
      });
    }

    if (
      !getFieldValue("selectableDays") ||
      getFieldValue("selectableDays").length < 1
    ) {
      newErrors.selectableDays = t("validation.required", {
        field: t("Training Frequency"),
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleApplyGoal = () => {
    if (!validateForm()) {
      toast.error(t("toast.fillFields"));
      return;
    }

    // Ensure all fields are in selectedTags
    const updatedTags = {
      ...selectedTags,
      adviseMeDescription: message,
      dailyCalorieAdjustment: calculatedDailyCalorieAdjustment,
    };

    handleAutoSubmit(updatedTags);
  };

  const startCountdown = () => {
    setCountdown(60);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 0) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const validateCommingData = (dataToValidate) => {
    setErrors({});

    const newErrors = {};

    const getFieldValue = (field) => dataToValidate[field];

    // targetWeight
    if (
      !getFieldValue("targetWeight") ||
      getFieldValue("targetWeight") === ""
    ) {
      newErrors.targetWeight = t("validation.required", {
        field: t(`firstFormSteps.targetWeight.label`),
      });
    }

    // maintenanceCalories
    if (
      !getFieldValue("maintenanceCalories") ||
      getFieldValue("maintenanceCalories") === ""
    ) {
      newErrors.maintenanceCalories = t("validation.required", {
        field: t(`firstFormSteps.maintenanceCalories.label`),
      });
    }

    // targetDailyCalories
    if (
      !getFieldValue("targetDailyCalories") ||
      getFieldValue("targetDailyCalories") === ""
    ) {
      newErrors.targetDailyCalories = t("validation.required", {
        field: t(`firstFormSteps.targetDailyCalories.label`),
      });
    }

    // timeline
    if (!getFieldValue("timeline") || getFieldValue("timeline") === "") {
      newErrors.timeline = t("validation.required", {
        field: t(`firstFormSteps.timeline.label`),
      });
    }

    // durationType
    if (
      !getFieldValue("durationType") ||
      getFieldValue("durationType") === ""
    ) {
      newErrors.durationType = t("validation.required", {
        field: t(`firstFormSteps.durationType.label`),
      });
    }

    // goalBodyFat
    if (!getFieldValue("goalBodyFat") || getFieldValue("goalBodyFat") === "") {
      newErrors.goalBodyFat = t("validation.required", {
        field: t("Goal Body Fat %"),
      });
    }

    // goalWaistCircumference
    if (
      !getFieldValue("goalWaistCircumference") ||
      getFieldValue("goalWaistCircumference") === ""
    ) {
      newErrors.goalWaistCircumference = t("validation.required", {
        field: t("Goal Waist Circumference (cm)"),
      });
    }

    if (!getFieldValue("goalSteps") || getFieldValue("goalSteps") === "") {
      newErrors.goalSteps = t("validation.required", {
        field: t(`preferences.fields.goalSteps`),
      });
    }
    if (
      !getFieldValue("detailTargetBurnCalories") ||
      getFieldValue("preferences.fields.detailTargetBurnCalories") === ""
    ) {
      newErrors.detailTargetBurnCalories = t("validation.required", {
        field: t("detailTargetBurnCalories"),
      });
    }
    if (
      !getFieldValue("targetActiveTime") ||
      getFieldValue("targetActiveTime") === ""
    ) {
      newErrors.targetActiveTime = t("validation.required", {
        field: t("preferences.fields.targetActiveTime"),
      });
    }

    // --- EXERCISE PREFERENCES VALIDATION ---

    // dailyExerciseTimePreference
    if (
      !getFieldValue("dailyExerciseTimePreference") ||
      getFieldValue("dailyExerciseTimePreference") === ""
    ) {
      newErrors.dailyExerciseTimePreference = t("validation.required", {
        field: t("Daily Exercise Time Preference"),
      });
    }

    // workoutsCategories (Requires at least one item in the array)
    if (
      !getFieldValue("workoutsCategories") ||
      getFieldValue("workoutsCategories").length < 1
    ) {
      newErrors.workoutsCategories = t("validation.required", {
        field: t("Workout Categories"),
      });
    }

    // equipmentCategories
    if (
      !getFieldValue("equipmentCategories") ||
      getFieldValue("equipmentCategories") === ""
    ) {
      newErrors.equipmentCategories = t("validation.required", {
        field: t("Equipment Category"),
      });
    }

    // difficultiesLevel
    if (
      !getFieldValue("difficultiesLevel") ||
      getFieldValue("difficultiesLevel") === ""
    ) {
      newErrors.difficultiesLevel = t("validation.required", {
        field: t("Difficulty Level"),
      });
    }

    // selectableDays (Training Frequency - Requires at least one item in the array)
    if (
      !getFieldValue("selectableDays") ||
      getFieldValue("selectableDays").length < 1
    ) {
      newErrors.selectableDays = t("validation.required", {
        field: t("Training Frequency"),
      });
    }

    // 2. Update the error state with any new errors found.
    setErrors(newErrors);

    // 3. Return true if no errors were found, false otherwise.
    return Object.keys(newErrors).length === 0;
  };

  const handleAdvice = async () => {
    setIsAdviceLoading(true);
    setIsActionLocked(true);
    setFormSubmitting(true);
    startCountdown();

    try {
      const response = await getAutoPreference({
        Height: selectedTags?.height,
        Weight: selectedTags?.weight,
        "Your Age": selectedTags?.yourAge,
        "Weight Goal": selectedTags?.weightGoal,
        "Gain Muscle": selectedTags?.gainMuscle,
        "Weight Unit": selectedTags?.weightUnit,
        "Target Weight Unit": selectedTags?.targetWeightUnit,
        activity: selectedTags?.activity,
      });

      const data = response?.data;

      // Update all fields in selectedTags at once
      const updatedTags = {
        ...selectedTags,
        durationType: data?.durationType,
        targetWeight: data?.finalTargetWeight,
        targetWeightUnit: data?.targetWeightUnit,
        goalWaistCircumference: data?.goalWaistCircumference,
        goalBodyFat: data?.goalBodyFat,
        timeline: data?.timeline,
        dailyCalorieAdjustment: data?.dailyCalorieAdjustment,
        maintenanceCalories: data?.maintenanceCalories,
        targetDailyCalories: data?.targetDailyCalories,
        workoutsCategories: data?.workoutsCategories,
        equipmentCategories: data?.equipmentCategories,
        difficultiesLevel: data?.difficultiesLevel,
        dailyExerciseTimePreference: data?.dailyExerciseTimePreference,
        selectableDays: data?.selectableDays,
        detailTargetBurnCalories: data?.detailTargetBurnCalories,
        goalSteps: data?.goalSteps,
        targetActiveTime: data?.targetActiveTime,
      };

      // 1. Update the state (asynchronously)
      setSelectedTags(updatedTags);

      // 2. 🌟 VALIDATE SYNCHRONOUSLY using the new object
      validateCommingData(updatedTags);

      setMessage(data?.message);
      toast.info(t("toast.adviceGenerated"));
    } catch (error) {
      console.log(error);
      toast.error(t("toast.Failed to get advice"));
    } finally {
      setIsAdviceLoading(false);
      setIsActionLocked(false);
      clearInterval(countdownRef.current);
      setCountdown(0);
    }
  };
  useEffect(() => {
    if (countdown === 2 && isAdviceLoading) {
      setCountdown((prev) => prev + 10);
    }
  }, [countdown, isAdviceLoading]);

  const handleWeightUnitChange = (newUnit) => {
    const currentWeight = getFieldValue("targetWeight");
    if (currentWeight) {
      const convertedWeight =
        newUnit === "Lbs" ? currentWeight * 2.20462 : currentWeight / 2.20462;
      setFieldValue("targetWeight", Math.round(convertedWeight));
    }
    setFieldValue("targetWeightUnit", newUnit);
  };

  const handleAutoSubmit = async (updatedTags) => {
    setAutoSubmit(true);
    try {
      await updateAllFormFields(updatedTags);
      toast.info(t("toast.informationSubmit"));
    } catch (error) {
      console.log(error);
      toast.error(t("toast.Failed to get advice"));
    } finally {
      setAutoSubmit(false);
      setFormSubmitting(false);
    }
  };

  const handleClose = (key) => {
    if (key === "cancel") {
      setFormSubmitting(false);
      setOpen(false);
    } else {
      handleAdvice();
      setOpen(false);
    }
  };

  const nutrientData = [
    {
      label: t("BMI & Category"),
      icon: <BmiCategory className="w-[24px] h-[24px]" />,
      tabColor: "#F0FFFF",
      value: message?.["BMI & Category"],
    },
    {
      label: t("Goal Overview"),
      icon: <GoalOverview className="w-[24px] h-[24px]" />,
      tabColor: "#FFF0FB",
      value: message?.["Goal Overview"],
    },
    {
      label: t("Nutrition Strategy"),
      icon: <NutritionStratergy className="w-[24px] h-[24px]" />,
      tabColor: "#FFF6F0",
      value: message?.["Nutrition Strategy"],
    },
    {
      label: t("Workout Focus"),
      icon: <WorkoutFocus className="w-[24px] h-[24px]" />,
      tabColor: "#F5FFF0",
      value: message?.["Workout Focus"],
    },
    {
      label: t("Long-Term Plan"),
      icon: <LongTerm className="w-[24px] h-[24px]" />,
      tabColor: "#F5FFF0",
      value: message?.["Long-Term Plan"],
    },
  ];

  // Helper functions for dynamic options
  const getDynamicDropdownOptions = (key) => {
    const options = dynamicOptions[key] || [];
    if (key === "difficultiesLevel") {
      return options.map((opt) => ({
        value: opt,
        label: opt.charAt(0).toUpperCase() + opt.slice(1),
      }));
    } else {
      return options.map((opt) => ({
        value: opt.id,
        label: opt.name,
      }));
    }
  };

  const getDisplayMap = (key) => {
    const options = dynamicOptions[key] || [];
    const displayMap = {};
    if (key === "difficultiesLevel") {
      options.forEach((opt) => {
        displayMap[opt] = opt.charAt(0).toUpperCase() + opt.slice(1);
      });
    } else {
      options.forEach((opt) => {
        displayMap[opt.id] = opt.name;
      });
    }
    return displayMap;
  };

  const getDisplayName = (key, categoryId = null) => {
    if (!categoryId || !dynamicOptions?.[key]) return categoryId || "";
    if (key === "difficultiesLevel") {
      return categoryId.charAt(0).toUpperCase() + categoryId.slice(1);
    }
    const item = dynamicOptions[key].find((item) => item?.id === categoryId);
    return item ? item.name : categoryId;
  };

  // Render editable input field
  const renderEditableInput = (
    field,
    label,
    unit = null,
    isReadOnly = false
  ) => {
    const value = getFieldValue(field);
    return (
      <div className="w-full">
        <span className="text-[#414651] text-[14px] sm:text-[16px] block mb-1">
          {label}
        </span>

        {field === "targetWeight" ? (
          <div className="relative">
            <TextField
              type="number"
              variant="outlined"
              fullWidth
              size="small"
              value={
                value === null || value === undefined || value === ""
                  ? ""
                  : Math.round(Number(value))
              }
              onChange={(e) => handleInputChange(field, e.target.value)}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
              }}
              disabled={isAdviceLoading || isReadOnly}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.875rem",
                  color: "black",
                  height: "40px",
                  "& fieldset": {
                    borderColor: errors[field] ? "#EF4444" : "#e5e7eb",
                    borderRadius: "0.375rem",
                  },
                  "&:hover fieldset": {
                    borderColor: errors[field] ? "#EF4444" : "#e5e7eb",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: errors[field] ? "#EF4444" : "#e5e7eb",
                    boxShadow: "none",
                    outline: "none",
                    borderWidth: "1px",
                  },
                  "& input::placeholder": {
                    fontSize: "0.9rem",
                    opacity: 1,
                    color: "#9CA3AF",
                  },
                  "&.Mui-disabled": {
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                      outline: "none",
                    },
                    "&:hover fieldset": {
                      borderColor: "#e5e7eb",
                      outline: "none",
                    },
                  },
                },
              }}
              InputProps={{
                endAdornment:
                  field === "targetWeight" ? (
                    <InputAdornment position="end">
                      <div className="flex items-center mr-1">
                        {["kg", "Lbs"].map((unit) => (
                          <button
                            key={unit}
                            onClick={() => handleWeightUnitChange(unit)}
                            className={`px-2 py-0.5 border rounded-md mx-0.5 text-xs ${
                              getFieldValue("targetWeightUnit") === unit
                                ? "bg-blue-500 text-white"
                                : "bg-white text-black border-gray-300"
                            }`}
                            type="button"
                            disabled={isAdviceLoading}
                          >
                            {unit}
                          </button>
                        ))}
                      </div>
                    </InputAdornment>
                  ) : unit ? (
                    <InputAdornment position="end">
                      <span className="text-gray-500 text-sm">{unit}</span>
                    </InputAdornment>
                  ) : null,
              }}
            />
          </div>
        ) : (
          <input
            type="number"
            min="1"
            className={`w-full border p-2 rounded-md h-10 text-sm focus:outline-none ${
              errors[field] ? "border-red-500 text-red-500" : "text-black"
            }`}
            value={
              value === null || value === undefined || value === ""
                ? ""
                : Math.round(Number(value))
            }
            onChange={(e) => handleInputChange(field, e.target.value)}
            onKeyDown={(e) => {
              if (["e", "E", "+", "-"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            placeholder={label}
            disabled={isAdviceLoading || isReadOnly}
          />
        )}
        {errors[field] && (
          <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
            {/* {errors[field]} */}
            {t("preferences.validation.fieldRequired", {
              field: label,
            })}
          </div>
        )}
      </div>
    );
  };

  // Render select field
  const renderSelectField = (field, label, options) => {
    const value = getFieldValue(field);

    return (
      <div className="w-full">
        <span className="text-[#414651] text-[14px] sm:text-[16px] block mb-1">
          {label}
        </span>
        <div className="relative">
          <select
            className={`border p-2 rounded-md w-full h-10 text-sm outline-none bg-white ${
              errors[field] ? "border-red-500" : ""
            } ${isAdviceLoading ? "opacity-70" : ""}`}
            value={value || ""}
            onChange={(e) => handleSelectChange(field, e.target.value)}
            disabled={isAdviceLoading}
          >
            <option value="" disabled>
              {t("preferences.validation.onlyOne")}
            </option>
            {options.map((option) => (
              <option key={option} value={option}>
                {field === "durationType"
                  ? t(`firstFormSteps.durationType.options.${option}`)
                  : option}
              </option>
            ))}
          </select>
          {errors[field] && (
            <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
              {errors[field]}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render dynamic select field
  const renderDynamicSelectField = (field, label) => {
    const value = getFieldValue(field);
    const options = getDynamicDropdownOptions(field);

    return (
      <div className="w-full">
        <span className="text-[#414651] text-[12px] font-semibold text-gray-700 uppercase block mb-1">
          {label}
        </span>
        <div className="relative">
          <select
            className={`border p-2 rounded-md w-full h-10 text-sm outline-none bg-white ${
              errors[field] ? "border-red-500" : ""
            } ${isAdviceLoading ? "opacity-70 cursor-default" : ""}`}
            value={value || ""}
            onChange={(e) => handleSelectChange(field, e.target.value)}
            disabled={isAdviceLoading}
          >
            <option value="" disabled>
              {t("preferences.validation.onlyOne")}
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors[field] && (
            <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
              {errors[field]}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleRemoveDay = (dayToRemove) => {
    const currentDays = getFieldValue("selectableDays") || [];
    const updatedDays = currentDays.filter((day) => day !== dayToRemove);
    handleMultiSelectChange("selectableDays", updatedDays);
  };

  const handleRemoveWorkoutCategory = (idToRemove) => {
    const currentCategories = getFieldValue("workoutsCategories") || [];
    const updatedCategories = currentCategories.filter(
      (id) => id !== idToRemove
    );
    handleMultiSelectChange("workoutsCategories", updatedCategories);
  };

  // -------------------------------------------------------------

  return (
    <>
      <NavigationPrompt
        when={isSubmitting || isAdviceLoading || formSubmitting}
      />

      {firstLoad ? (
        <ActualDietPlanLoader adviseMe={true} />
      ) : (
        <>
          <MainHeader
            heading={t(`Get Personalized Health Advice Instantly`)}
            description={t("firstFormSteps.adviseMeDescription")}
            Image={HeaderImage}
            adviseMe={true}
          />

          <div className="bg-white p-3 rounded-[10px] mt-7">
            <h2 className="text-[20px] md:text-[24px] md:text-center font-[600] font-[Lato] text-black-600 px-4">
              {t("firstFormSteps.lastStepHeading")}
            </h2>
            <p className="text-[#535862] text-[16px] md:text-center px-4">
              {t("firstFormSteps.lastStepSubHeading")}
            </p>

            <div className="flex justify-center mt-2">
              <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 md:gap-3 w-full">
                {/* Left Column - Input Fields */}
                <div className="xl:col-span-3 p-4">
                  <h2 className="text-[16px] md:text-[20px] font-[600] font-[Lato] text-black-600 mb-4">
                    {t("firstFormSteps.automaticGoalHeading")}
                  </h2>

                  {/* Get Advice Button */}
                  <div className="mb-6">
                    <button
                      type="button"
                      onClick={() => setOpen(true)}
                      className={`px-4 py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-md w-full ${
                        isAdviceLoading && "opacity-70"
                      }`}
                      disabled={isAdviceLoading}
                    >
                      {isAdviceLoading
                        ? `(${countdown}s)`
                        : t("firstFormSteps.getAdvise")}
                    </button>
                  </div>

                  {/* Manual Goal Heading */}
                  <h2 className="text-[16px] md:text-[20px] font-[600] font-[Lato] my-4 text-center font-[600] text-black-600">
                    {t("firstFormSteps.manualGoal")}
                  </h2>

                  {/* Input Fields Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* First row: Target Weight, Timeline */}
                    <div>
                      {renderEditableInput(
                        "targetWeight",
                        t(`firstFormSteps.targetWeight.label`)
                      )}
                    </div>

                    {/* Second row: Goal Body Fat, Goal Waist Circumference */}
                    <div>
                      {renderEditableInput(
                        "goalBodyFat",
                        t("Goal Body Fat"),
                        "%"
                      )}
                    </div>

                    <div>
                      {renderEditableInput(
                        "goalWaistCircumference",
                        t("Goal Waist Circumference"),
                        "cm"
                      )}
                    </div>

                    {/* Third row: Duration Type, Maintenance Calories */}

                    <div>
                      {renderEditableInput(
                        "maintenanceCalories",
                        t(`firstFormSteps.maintenanceCalories.label`)
                      )}
                    </div>

                    {/* Fourth row: Target Daily Calories, Daily Calorie Adjustment */}
                    <div>
                      {renderEditableInput(
                        "targetDailyCalories",
                        t(`firstFormSteps.targetDailyCalories.label`)
                      )}
                    </div>

                    <div>
                      {renderEditableInput(
                        "dailyCalorieAdjustment",
                        t(`preferences.fields.dailyCalorieAdjustment`),
                        "Kcal",
                        true
                      )}
                    </div>
                    <div>
                      {renderEditableInput(
                        "goalSteps",
                        t(`preferences.fields.goalSteps`)
                      )}
                    </div>

                    <div>
                      {renderEditableInput(
                        "detailTargetBurnCalories",
                        t(`preferences.fields.detailTargetBurnCalories`)
                      )}
                    </div>

                    <div>
                      {renderEditableInput(
                        "targetActiveTime",
                        t(`preferences.fields.targetActiveTime`)
                      )}
                    </div>

                    <div>
                      {renderSelectField(
                        "timeline",
                        t(`firstFormSteps.timeline.label`),
                        timelineOptions
                      )}
                    </div>
                    <div>
                      {renderSelectField(
                        "durationType",
                        t(`firstFormSteps.durationType.label`),
                        durationTypeOptions
                      )}
                    </div>
                  </div>

                  {/* Exercise Preferences Section */}

                  {/* Apply Button */}
                </div>

                {/* Right Column - Professional Advice */}
                <div className="xl:col-span-2 p-4 xl:mt-[42px]">
                  {message ? (
                    <div className="py-4 px-2 md:px-7 rounded-lg bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] min-h-[450px] ">
                      <div className="flex justify-center mb-4">
                        <p className="bg-[#64DDFF] text-white rounded-full text-[16px] xl:text-[20px] font-[Lato] font-[600] py-1 px-4 whitespace-nowrap overflow-hidden text-ellipsis">
                          {t("Our Professional Advice For You")}
                        </p>
                      </div>

                      <div
                        id="scrollable-quiz-container"
                        className="h-full max-h-[500px] overflow-y-auto pr-2 custom-scrollbar space-y-2"
                      >
                        {nutrientData.map(
                          ({ label, icon, value, tabColor }, index) => (
                            <div
                              key={index}
                              className="py-4 px-4 md:py-5 md:px-6 rounded-[16px] bg-white border border-[#E6E6E6] flex flex-col gap-2"
                              style={{
                                boxShadow: "7px 7px 9.5px 0px #0000000D",
                              }}
                            >
                              <div className="relative">
                                <div
                                  className={`absolute border border-[#0575e6] -left-3 -top-1 rounded-full min-w-12 min-h-12 flex items-center justify-center border`}
                                  style={{ backgroundColor: tabColor }}
                                >
                                  {icon}
                                </div>
                                <p className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-[14px] lg:text-[16px] font-[600] text-[#fff] py-2 px-8 md:px-10 rounded-full text-center lg:text-start min-w-[180px] md:min-w-[200px] max-w-full">
                                  {label}
                                </p>
                              </div>
                              <div className="mt-2 md:mt-0">
                                <p className="text-[14px] lg:text-[16px] font-[500] text-[#6C6A6A] font-[Lato] leading-relaxed">
                                  {value}
                                </p>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <img
                        src={AdviseMeSidePic}
                        alt="Advise Me"
                        className="object-cover rounded-[15px] w-full min-h-[500px] max-h-[638px]"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t("Exercise Preferences")}
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Workout Categories Card */}
                <div className="col-span-2 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <SportsGymnasticsIcon className="text-blue-500 mr-2" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {t("Workout Categories")}
                    </span>
                  </div>
                  <ExercisePlanMultiSelectDropdown
                    options={getDynamicDropdownOptions(
                      "workoutsCategories"
                    ).map((opt) => opt.value)}
                    selectedValues={getFieldValue("workoutsCategories") || []}
                    onSelect={(values) =>
                      handleMultiSelectChange("workoutsCategories", values)
                    }
                    label={t("Workout Categories")}
                    hasError={!!errors.workoutsCategories}
                    errorMessage={errors.workoutsCategories}
                    placeholder={t("preferences.validation.onlyOne")}
                    isEditable={!isAdviceLoading}
                    isFistForm={true}
                    displayMap={getDisplayMap("workoutsCategories")}
                    fieldKey="workoutsCategories"
                  />
                  <div className="mt-2 flex flex-wrap gap-1">
                    {getFieldValue("workoutsCategories") &&
                      getFieldValue("workoutsCategories").length > 0 &&
                      getFieldValue("workoutsCategories").map((categoryId) => (
                        <span
                          key={categoryId}
                          className="
                    px-2 py-1 text-xs font-medium rounded-md
                    bg-blue-100 text-blue-800 border border-blue-200
                    flex items-center space-x-1 cursor-pointer 
                    transition duration-150 ease-in-out hover:bg-blue-200
                "
                        >
                          {/* Display the name */}
                          <span>
                            {getDisplayName("workoutsCategories", categoryId)}
                          </span>

                          {/* The Removal Button */}
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveWorkoutCategory(categoryId)
                            }
                            className="
                        ml-1 w-4 h-4 rounded-full
                        text-blue-600 hover:text-blue-800
                        focus:outline-none focus:ring-2 focus:ring-blue-500
                    "
                            aria-label={`Remove workout category`}
                            disabled={isAdviceLoading}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                              ></path>
                            </svg>
                          </button>
                        </span>
                      ))}
                  </div>
                </div>

                {/* Equipment Category Card */}
                <div className="col-span-2 lg:col-span-1 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <FitnessCenterIcon className="text-green-500 mr-2" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {t("Equipment Category")}
                    </span>
                  </div>
                  {renderDynamicSelectField(
                    "equipmentCategories"
                    // t("Equipment Category")
                  )}
                  <div className="mt-2">
                    {getFieldValue("equipmentCategories") && (
                      <span className="px-2 py-1 text-xs font-medium rounded-md bg-green-100 text-green-800 border border-green-200">
                        {getDisplayName(
                          "equipmentCategories",
                          getFieldValue("equipmentCategories")
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {/* Difficulty Level Card */}
                <div className="col-span-2 lg:col-span-1 p-4 bg-purple-50 border-l-4 border-purple-500 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <TuneIcon className="text-purple-500 mr-2" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {t("Difficulty Level")}
                    </span>
                  </div>
                  {renderDynamicSelectField(
                    "difficultiesLevel"
                    // t("Difficulty Level")
                  )}
                  <div className="mt-2">
                    {getFieldValue("difficultiesLevel") && (
                      <span className="px-2 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-800 border border-purple-200">
                        {getDisplayName(
                          "difficultiesLevel",
                          getFieldValue("difficultiesLevel")
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {/* Daily Time Preference Card */}
                <div className="col-span-2 p-4 bg-orange-50 border-l-4 border-orange-500 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <AccessTimeIcon className="text-orange-500 mr-2" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {t("Daily Exercise Time Preference")}
                    </span>
                  </div>
                  <div className="relative">
                    <select
                      className={`border p-2 rounded-md w-full h-10 text-sm outline-none bg-white ${
                        errors.dailyExerciseTimePreference
                          ? "border-red-500"
                          : ""
                      } ${isAdviceLoading ? "opacity-70 cursor-default" : ""}`}
                      value={getFieldValue("dailyExerciseTimePreference") || ""}
                      onChange={(e) =>
                        handleSelectChange(
                          "dailyExerciseTimePreference",
                          e.target.value
                        )
                      }
                      disabled={isAdviceLoading}
                    >
                      <option value="" disabled>
                        {t("preferences.validation.onlyOne")}
                      </option>
                      {exerciseTimeOptions.map((option) => (
                        <option key={option} value={option}>
                          {/* {option} */}
                          {t(
                            `allPreferencesModule.options.dailyExerciseTimePreference.${option}`
                          )}
                        </option>
                      ))}
                    </select>
                    {errors.dailyExerciseTimePreference && (
                      <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                        {errors.dailyExerciseTimePreference}
                      </div>
                    )}
                  </div>
                  <div className="mt-2">
                    {getFieldValue("dailyExerciseTimePreference") && (
                      <span className="px-2 py-1 text-xs font-medium rounded-md bg-orange-100 text-orange-800 border border-orange-200">
                        {/* {getFieldValue("dailyExerciseTimePreference")} */}

                        {t(
                          `allPreferencesModule.options.dailyExerciseTimePreference.${getFieldValue(
                            "dailyExerciseTimePreference"
                          )}`
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {/* Selectable Days Card */}
                <div className="col-span-2 md:col-span-2 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <CalendarMonthIcon className="text-red-500 mr-2" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {t("Selectable Days")}
                    </span>
                  </div>
                  <MedicalViewMultipSelectDropdown
                    options={daysOptions?.map((opt) => ({
                      value: opt,
                      label: t(
                        `allPreferencesModule.options.selectableDays.${opt}`
                      ),
                    }))}
                    selectedValues={getFieldValue("selectableDays") || []}
                    onSelect={(selectedOptions) => {
                      const originalValues = selectedOptions.map((opt) =>
                        typeof opt === "object" ? opt.value : opt
                      );
                      handleMultiSelectChange("selectableDays", originalValues);
                    }}
                    isFistForm={true}
                    hasError={!!errors.selectableDays}
                    errorMessage={errors.selectableDays}
                    label={t("Selectable Days")}
                    placeholder={t("preferences.validation.onlyOne")}
                    isEditable={!isAdviceLoading}
                  />
                  <div className="mt-2 flex flex-wrap gap-1">
                    {getFieldValue("selectableDays") &&
                      getFieldValue("selectableDays").length > 0 &&
                      getFieldValue("selectableDays").map((day) => (
                        <span
                          key={day}
                          className="
                    px-2 py-1 text-xs font-medium rounded-md
                    bg-red-100 text-red-800 border border-red-200
                    flex items-center space-x-1 cursor-pointer 
                    transition duration-150 ease-in-out hover:bg-red-200
                "
                        >
                          <span>
                            {/* {day} */}

                            {t(
                              `allPreferencesModule.options.selectableDays.${day}`
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveDay(day)}
                            className="
                        ml-1 w-4 h-4 rounded-full
                        text-red-600 hover:text-red-800
                        focus:outline-none focus:ring-2 focus:ring-red-500
                    "
                            aria-label={`Remove ${day}`}
                            disabled={isAdviceLoading}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                              ></path>
                            </svg>
                          </button>
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleApplyGoal}
              disabled={isAdviceLoading || autoSubmit}
              className={`mt-6 px-4 py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-md w-full min-h-[46px] ${
                (isAdviceLoading || autoSubmit) && "opacity-70"
              }`}
            >
              {autoSubmit ? (
                <ButtonLoader />
              ) : (
                t("firstFormSteps.navigationButtons.submit")
              )}
            </button>
          </div>
        </>
      )}

      {/* Confirmation Dialog */}
      <ConfirmAdvise
        open={open}
        handleClose={handleClose}
        isLoading={isAdviceLoading}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        dynamicOptions={dynamicOptions}
        heading={t("firstFormSteps.Confirm Personalized Advice")}
        message={t(
          "firstFormSteps.Would you like to get personalized advice based on the fields below?"
        )}
        adviseMe={true}
      />
    </>
  );
};

export default AdviseMe;
